/**
 * Shared AI Session Logger
 *
 * Host-agnostic logger used by both PPT and Excel add-ins.
 * Persists logs to the local log-server for debugging/replay flows.
 */
import { getLoggingConfig, isLoggingEnabled } from "./config";
function inferHostFromSessionId(sessionId) {
    if (!sessionId)
        return undefined;
    if (sessionId.startsWith("excel_"))
        return "excel";
    if (sessionId.startsWith("ppt_") ||
        sessionId.startsWith("ppt-test_") ||
        sessionId.startsWith("ppt_test_")) {
        return "ppt";
    }
    return undefined;
}
function generateSessionId(host = "ppt") {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const random = Math.random().toString(36).substring(2, 8);
    return `${host}_${timestamp}_${random}`;
}
function getTimestamp() {
    return new Date().toISOString();
}
function truncateString(str, maxLength) {
    if (str.length <= maxLength)
        return str;
    return `${str.slice(0, maxLength)}... (${str.length - maxLength} more chars)`;
}
const FULL_RESULT_THRESHOLD = 100000;
function sanitizeForLogging(obj, maxStringLength = 10000, preserveLogImages = true) {
    if (obj === null || obj === undefined)
        return obj;
    if (typeof obj === "string") {
        if (obj.startsWith("data:image/") || obj.length > maxStringLength) {
            return `<${obj.length} chars truncated>`;
        }
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => sanitizeForLogging(item, maxStringLength, preserveLogImages));
    }
    if (typeof obj === "object") {
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
            if ((key === "_logImages" || key === "_thinkingMetadata") &&
                preserveLogImages) {
                sanitized[key] = value;
            }
            else {
                sanitized[key] = sanitizeForLogging(value, maxStringLength, preserveLogImages);
            }
        }
        return sanitized;
    }
    return obj;
}
function isLargeResult(result) {
    try {
        return JSON.stringify(result).length > FULL_RESULT_THRESHOLD;
    }
    catch {
        return false;
    }
}
class SessionLogger {
    constructor() {
        this.currentSession = null;
        this.turnNumber = 0;
        this.toolStartTimes = new Map();
        this.llmStartTime = null;
        this.accumulatedResponse = new Map();
        this.batchTimeout = null;
        this.pendingSave = false;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // SESSION LIFECYCLE
    // ═══════════════════════════════════════════════════════════════════════
    startSession(sessionId, metadata) {
        if (!isLoggingEnabled()) {
            return "";
        }
        const host = metadata?.host || inferHostFromSessionId(sessionId) || "ppt";
        const id = sessionId || generateSessionId(host);
        const initialMetadata = {
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
            platform: typeof navigator !== "undefined" ? navigator.platform : undefined,
            host,
            ...metadata,
        };
        // Keep legacy PPT fields mirrored when we receive generic fields.
        if (!initialMetadata.presentationName && initialMetadata.documentName) {
            initialMetadata.presentationName = initialMetadata.documentName;
        }
        if (typeof initialMetadata.totalSlides !== "number" &&
            typeof initialMetadata.documentUnitCount === "number") {
            initialMetadata.totalSlides = initialMetadata.documentUnitCount;
        }
        this.currentSession = {
            sessionId: id,
            startedAt: getTimestamp(),
            metadata: initialMetadata,
            summary: {
                totalTurns: 0,
                totalLlmCalls: 0,
                totalToolCalls: 0,
                totalErrors: 0,
                totalDurationMs: 0,
                toolsUsed: [],
                agentsUsed: [],
            },
            turns: [],
        };
        this.turnNumber = 0;
        this.toolStartTimes.clear();
        this.accumulatedResponse.clear();
        this.pendingSave = false;
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }
        this.addTurn("system", {
            event: "session_start",
            message: `Session started: ${id}`,
            context: { host },
        });
        console.log(`[SharedSessionLogger] Started session: ${id} (${host})`);
        return id;
    }
    async endSession() {
        if (!this.currentSession)
            return;
        this.flushAccumulatedResponses();
        this.currentSession.completedAt = getTimestamp();
        const startMs = new Date(this.currentSession.startedAt).getTime();
        const endMs = new Date(this.currentSession.completedAt).getTime();
        this.currentSession.summary.totalDurationMs = endMs - startMs;
        this.addTurn("system", {
            event: "session_end",
            message: `Session ended. Duration: ${this.currentSession.summary.totalDurationMs}ms`,
            context: {
                totalTurns: this.currentSession.summary.totalTurns,
                totalLlmCalls: this.currentSession.summary.totalLlmCalls,
                totalToolCalls: this.currentSession.summary.totalToolCalls,
                totalErrors: this.currentSession.summary.totalErrors,
            },
        });
        if (this.batchTimeout) {
            clearTimeout(this.batchTimeout);
            this.batchTimeout = null;
        }
        await this.persistSession();
        this.saveToLocalStorage();
        console.log(`[SharedSessionLogger] Ended session: ${this.currentSession.sessionId}`);
        this.currentSession = null;
        this.pendingSave = false;
    }
    getCurrentTurnNumber() {
        return this.turnNumber;
    }
    getSessionId() {
        return this.currentSession?.sessionId ?? null;
    }
    isSessionActive() {
        return this.currentSession !== null;
    }
    getCurrentSession() {
        return this.currentSession;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // LOGGING METHODS
    // ═══════════════════════════════════════════════════════════════════════
    logUserMessage(message, slideNumber, totalSlides) {
        if (!this.currentSession)
            return;
        if (totalSlides !== undefined) {
            this.currentSession.metadata.totalSlides = totalSlides;
            this.currentSession.metadata.documentUnitCount = totalSlides;
        }
        this.addTurn("user_message", {
            message: truncateString(message, 5000),
            slideNumber,
            totalSlides,
            documentPosition: slideNumber,
            documentUnitCount: totalSlides,
        });
    }
    logLLMRequest(agentName, messageCount, toolCount, model, systemPromptLength, requestPayload) {
        if (!this.currentSession)
            return;
        this.llmStartTime = Date.now();
        if (!this.currentSession.summary.agentsUsed.includes(agentName)) {
            this.currentSession.summary.agentsUsed.push(agentName);
        }
        const data = {
            agentName,
            model,
            messageCount,
            toolCount,
            systemPromptLength,
        };
        if (requestPayload) {
            data.requestPayload = {
                systemPrompt: requestPayload.systemPrompt,
                messages: requestPayload.messages
                    ? sanitizeForLogging(requestPayload.messages, 50000, true)
                    : undefined,
                toolChoice: requestPayload.toolChoice,
                maxTokens: requestPayload.maxTokens,
                temperature: requestPayload.temperature,
                extraBody: requestPayload.extraBody,
                extraHeaders: requestPayload.extraHeaders,
                thinking: requestPayload.thinking,
            };
        }
        this.addTurn("orchestrator_request", data);
        this.currentSession.summary.totalLlmCalls++;
    }
    accumulateChunk(agentName, chunk) {
        if (!this.currentSession)
            return;
        const existing = this.accumulatedResponse.get(agentName) || "";
        this.accumulatedResponse.set(agentName, existing + chunk);
    }
    logLLMResponse(agentName, content, tokensUsed, toolCalls, finishReason) {
        if (!this.currentSession)
            return;
        const duration = this.llmStartTime
            ? Date.now() - this.llmStartTime
            : undefined;
        this.accumulatedResponse.delete(agentName);
        const data = {
            agentName,
            content: truncateString(content, 10000),
            contentLength: content.length,
            tokensUsed,
            toolCallsCount: toolCalls?.length ?? 0,
            finishReason,
        };
        if (toolCalls && toolCalls.length > 0) {
            data.toolCalls = toolCalls.map((tc) => ({
                id: tc.id,
                name: tc.name,
                argsPreview: tc.arguments
                    ? truncateString(tc.arguments, 200)
                    : undefined,
            }));
        }
        this.addTurn("orchestrator_response", data, duration);
        this.llmStartTime = null;
    }
    logToolStart(agentName, toolName, args, toolId, isAgent) {
        if (!this.currentSession)
            return;
        const id = toolId || `${toolName}_${Date.now()}`;
        this.toolStartTimes.set(id, Date.now());
        if (!this.currentSession.summary.toolsUsed.includes(toolName)) {
            this.currentSession.summary.toolsUsed.push(toolName);
        }
        this.addTurn("tool_start", {
            agentName,
            toolName,
            toolId: id,
            isAgent,
            arguments: args
                ? sanitizeForLogging(args)
                : {},
        });
        this.currentSession.summary.totalToolCalls++;
    }
    logToolComplete(agentName, toolName, result, toolId) {
        if (!this.currentSession)
            return;
        const id = toolId || `${toolName}_${Date.now()}`;
        const startTime = this.toolStartTimes.get(id);
        const duration = startTime ? Date.now() - startTime : undefined;
        this.toolStartTimes.delete(id);
        const nextTurnNumber = this.turnNumber + 1;
        const resultObj = result && typeof result === "object"
            ? result
            : undefined;
        const isBlocked = resultObj?.blocked === true;
        const isFailure = resultObj?.success === false;
        const data = {
            agentName,
            toolName,
            toolId: id,
            success: !(isBlocked || isFailure),
        };
        if (result !== undefined) {
            if (isLargeResult(result)) {
                data.fullResultId = `turn_${nextTurnNumber}_${toolName}`;
                data.resultSize = JSON.stringify(result).length;
                void this.saveFullResult(nextTurnNumber, toolName, result);
            }
            data.result = sanitizeForLogging(result);
        }
        this.addTurn("tool_complete", data, duration);
    }
    logToolError(agentName, toolName, error, toolId, args) {
        if (!this.currentSession)
            return;
        const id = toolId || `${toolName}_${Date.now()}`;
        const startTime = this.toolStartTimes.get(id);
        const duration = startTime ? Date.now() - startTime : undefined;
        this.toolStartTimes.delete(id);
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorStack = error instanceof Error ? error.stack : undefined;
        const data = {
            agentName,
            toolName,
            toolId: id,
            error: errorMessage,
            errorType: error instanceof Error ? error.name : "Error",
            stack: errorStack,
            arguments: args
                ? sanitizeForLogging(args)
                : undefined,
        };
        this.addTurn("tool_error", data, duration);
        this.currentSession.summary.totalErrors++;
    }
    logAgentDelegation(parentAgent, childAgent, requirements) {
        if (!this.currentSession)
            return;
        this.addTurn("agent_delegation", {
            parentAgent,
            childAgent,
            requirements,
        });
    }
    logAgentResult(parentAgent, childAgent, status, resultCount, error) {
        if (!this.currentSession)
            return;
        this.addTurn("agent_result", {
            parentAgent,
            childAgent,
            status,
            resultCount,
            error,
        });
        if (status === "error") {
            this.currentSession.summary.totalErrors++;
        }
    }
    async logSlideImage(slideNumber, imageType, context, imageBase64, toolName) {
        if (!this.currentSession)
            return undefined;
        const config = getLoggingConfig();
        if (!config.captureSlideImages)
            return undefined;
        const imageName = `slide${slideNumber}_${imageType}_${toolName || "capture"}_t${this.turnNumber + 1}.png`;
        let imagePath;
        try {
            const response = await this.uploadImage(imageName, imageBase64);
            imagePath = response?.imagePath;
        }
        catch (e) {
            console.error("[SharedSessionLogger] Failed to upload image:", e);
        }
        const captureType = imageType === "current" ? "snapshot" : imageType;
        this.addTurn("slide_image", {
            slideNumber,
            captureType,
            operationContext: context,
            toolName,
            imagePath,
        });
        return imagePath;
    }
    logError(source, error, context) {
        if (!this.currentSession)
            return;
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorStack = error instanceof Error ? error.stack : undefined;
        this.addTurn("error", {
            source,
            error: errorMessage,
            errorType: error instanceof Error ? error.name : "Error",
            stack: errorStack,
            context: context
                ? sanitizeForLogging(context)
                : undefined,
        });
        this.currentSession.summary.totalErrors++;
    }
    logSystem(event, message, context) {
        if (!this.currentSession)
            return;
        this.addTurn("system", {
            event,
            message,
            context: context
                ? sanitizeForLogging(context)
                : undefined,
        });
    }
    setPresentationName(name) {
        if (!this.currentSession)
            return;
        this.currentSession.metadata.presentationName = name;
        this.currentSession.metadata.documentName = name;
    }
    setMetadata(metadata) {
        if (!this.currentSession)
            return;
        this.currentSession.metadata = {
            ...this.currentSession.metadata,
            ...metadata,
        };
        if (!this.currentSession.metadata.presentationName &&
            this.currentSession.metadata.documentName) {
            this.currentSession.metadata.presentationName =
                this.currentSession.metadata.documentName;
        }
        if (typeof this.currentSession.metadata.totalSlides !== "number" &&
            typeof this.currentSession.metadata.documentUnitCount === "number") {
            this.currentSession.metadata.totalSlides =
                this.currentSession.metadata.documentUnitCount;
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // INTERNAL HELPERS
    // ═══════════════════════════════════════════════════════════════════════
    addTurn(type, data, durationMs) {
        if (!this.currentSession)
            return;
        const turn = {
            turnNumber: this.turnNumber + 1,
            timestamp: getTimestamp(),
            type,
            data,
            durationMs,
        };
        // Copy agentName to top-level for easier filtering
        if (typeof data === "object" && data !== null && "agentName" in data) {
            const maybeAgentName = data.agentName;
            if (typeof maybeAgentName === "string") {
                turn.agentName = maybeAgentName;
            }
        }
        this.currentSession.turns.push(turn);
        this.turnNumber++;
        this.currentSession.summary.totalTurns = this.turnNumber;
        this.schedulePersist();
    }
    flushAccumulatedResponses() {
        if (!this.currentSession)
            return;
        for (const [agentName, content] of this.accumulatedResponse.entries()) {
            if (content.trim()) {
                this.logLLMResponse(agentName, content);
            }
        }
        this.accumulatedResponse.clear();
    }
    schedulePersist() {
        if (!this.currentSession || !isLoggingEnabled())
            return;
        const config = getLoggingConfig();
        // Force save on batch size threshold
        const turnsSinceLastSave = this.currentSession.turns.length;
        if (turnsSinceLastSave >= config.batchSize && !this.pendingSave) {
            this.pendingSave = true;
            void this.persistSession().finally(() => {
                this.pendingSave = false;
            });
            return;
        }
        // Debounced save
        if (this.batchTimeout)
            clearTimeout(this.batchTimeout);
        this.batchTimeout = setTimeout(() => {
            if (this.pendingSave)
                return;
            this.pendingSave = true;
            void this.persistSession().finally(() => {
                this.pendingSave = false;
            });
        }, config.batchIntervalMs);
    }
    async persistSession() {
        if (!this.currentSession || !isLoggingEnabled())
            return;
        const config = getLoggingConfig();
        const request = {
            sessionId: this.currentSession.sessionId,
            sessionData: this.currentSession,
        };
        try {
            const response = await fetch(`${config.serverUrl}/api/save_session_log`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(request),
            });
            if (!response.ok) {
                const detail = await response.text().catch(() => "");
                console.warn(`[SharedSessionLogger] Failed to persist session ${this.currentSession.sessionId}: ${response.status}${detail ? ` - ${detail}` : ""}`);
            }
        }
        catch (error) {
            console.warn("[SharedSessionLogger] Error persisting session:", error);
        }
    }
    async saveFullResult(turnNumber, toolName, result) {
        if (!this.currentSession)
            return null;
        const config = getLoggingConfig();
        const resultId = `turn_${turnNumber}_${toolName}`;
        const request = {
            sessionId: this.currentSession.sessionId,
            resultId,
            resultData: result,
        };
        try {
            const response = await fetch(`${config.serverUrl}/api/save_full_result`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(request),
            });
            if (response.ok) {
                return resultId;
            }
            return null;
        }
        catch {
            return null;
        }
    }
    async uploadImage(imageName, imageData) {
        if (!this.currentSession)
            return null;
        const config = getLoggingConfig();
        const request = {
            sessionId: this.currentSession.sessionId,
            imageName,
            imageData,
        };
        const response = await fetch(`${config.serverUrl}/api/save_session_image`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(request),
        });
        if (!response.ok) {
            const detail = await response.text().catch(() => "");
            throw new Error(`Failed to upload image: ${response.status}${detail ? ` - ${detail}` : ""}`);
        }
        const payload = (await response.json());
        if (!payload.data?.imagePath) {
            throw new Error("Image upload succeeded but imagePath was missing");
        }
        return { imagePath: payload.data.imagePath };
    }
    saveToLocalStorage() {
        if (!this.currentSession)
            return;
        const config = getLoggingConfig();
        if (!config.useLocalStorageBackup || typeof localStorage === "undefined")
            return;
        try {
            const key = `${config.localStoragePrefix}${this.currentSession.sessionId}`;
            localStorage.setItem(key, JSON.stringify(this.currentSession));
            // Cleanup oldest backups beyond threshold
            const keys = [];
            for (let i = 0; i < localStorage.length; i++) {
                const itemKey = localStorage.key(i);
                if (itemKey && itemKey.startsWith(config.localStoragePrefix)) {
                    keys.push(itemKey);
                }
            }
            if (keys.length > config.maxLocalStorageSessions) {
                keys
                    .sort()
                    .slice(0, keys.length - config.maxLocalStorageSessions)
                    .forEach((oldKey) => localStorage.removeItem(oldKey));
            }
        }
        catch (e) {
            console.warn("[SharedSessionLogger] Failed localStorage backup:", e);
        }
    }
}
let sessionLoggerInstance = null;
export function getSessionLogger() {
    if (!sessionLoggerInstance) {
        sessionLoggerInstance = new SessionLogger();
    }
    return sessionLoggerInstance;
}
export { SessionLogger };
